import { useCallback, useEffect, useState } from 'react';

import { MediaRequestStatusFilter } from '@cobliteam/common-types';

import { HourglassIcon } from '@cobliteam/base-components';
import { Checkbox } from '@cobliteam/base-components/src/SelectionControls/Checkbox';

import Button from '../../../components/Button';
import Dropdown from '../../../components/Dropdown';

export interface MultiSelectMediaSeverityProps {
  isOpen?: boolean;
  size?: 'condensed' | 'regular';
  selectedOptions?: string[];
  onChange: (selectedOptions: string[]) => void;
}

const getEventSeverityLabel = (status: MediaRequestStatusFilter) => {
  switch (status) {
    case MediaRequestStatusFilter.NEW:
      return 'Novos vídeos';
    case MediaRequestStatusFilter.PENDING:
      return 'Solicitação pendente';
    default:
      return 'Vídeos assistidos';
  }
};

const MultiSelectMediaRequestStatus = ({
  isOpen,
  size = 'regular',
  selectedOptions = [],
  onChange,
}: MultiSelectMediaSeverityProps) => {
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedOptions || []);

  // check if all selected types are in draft
  const hasSameElements =
    selectedOptions?.length === draft?.length &&
    selectedOptions.every((value) => draft.find((d) => d === value));

  const onClick = useCallback(() => {
    setDraft(selectedOptions || []);
  }, [selectedOptions]);

  const onChangeSelect = (type: string) => {
    const isSelected = draft.find((d) => d === type);
    const updatedSelected = isSelected
      ? draft.filter((d) => d !== type)
      : [...draft, type];
    setDraft(updatedSelected as MediaRequestStatusFilter[]);
  };

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setDraft([]);
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  useEffect(() => setForceClosed(!isOpen), [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  return (
    <Dropdown
      onOpenChange={onClick}
      forceOpen={isOpen}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-5'>
            <Button
              variant='text'
              disabled={draft?.length === 0}
              onClick={() => setDraft([])}
            >
              Limpar seleção
            </Button>
          </Dropdown.Header>
          <Dropdown.Body>
            <Dropdown.List
              heightList={120}
              heightItem={40}
              data={Object.values(MediaRequestStatusFilter)}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item) => (
                <Checkbox
                  className='ml-5'
                  key={item}
                  checked={draft.includes(item)}
                  onChange={() => onChangeSelect(item)}
                  label={getEventSeverityLabel(item)}
                />
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-5'>
              <Button
                full
                variant='text'
                size='condensed'
                onClick={handleClose}
              >
                Cancelar
              </Button>
              <Button
                full
                variant='contained'
                size='condensed'
                color='primary'
                onClick={handleApply}
                disabled={hasSameElements}
              >
                Aplicar
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='text' startIcon={<HourglassIcon />}>
        Status{' '}
        {(selectedOptions?.length || 0) > 0
          ? `(${selectedOptions?.length})`
          : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectMediaRequestStatus;
